import React, { useEffect } from 'react';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { contentCountsUpToDate } from '~src/spacesPlatform/actions/ContentCountsActions';
import { spaceContentsRequestSucceeded } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { IAdminProfileSite } from '../../models/IAdminProfile';
import { AdminProfileSiteManagementButton } from '../AdminProfileSiteManagementButton';

export interface AdminProfileSitesTabProviderProps {
  sites: ReadonlyArray<IAdminProfileSite>;
  profileId: string;
  profileName: string;
  onProfileSitesUpdated?: () => void;
}

/** Minimal site-shaped object for Admin Profile list view. SiteNameLink expects settings.urlName and settings.name. */
type AdminProfileSiteContentInfo = IContentInfo<'site'> & {
  settings: { urlName: string, name: string };
  adminProfileAssignedUserCount: number;
};

function convertSiteToContentInfo(site: IAdminProfileSite): AdminProfileSiteContentInfo {
  return {
    id: site.id,
    contentType: 'site',
    name: site.name,
    luid: site.id,
    favorite: false,
    updatedAt: new Date(),
    userCount: 0,
    adminProfileAssignedUserCount: site.adminProfileAssignedUserCount,
    settings: {
      urlName: site.name,
      name: site.name,
    },
  } as AdminProfileSiteContentInfo;
}

function adminProfileSiteManagementButton(props: {
  profileId: string;
  profileName: string;
  sites: ReadonlyArray<IAdminProfileSite>;
  onProfileSitesUpdated?: () => void;
}): JSX.Element {
  return <AdminProfileSiteManagementButton {...props} />;
}

export const AdminProfileSitesTabProvider: React.FC<AdminProfileSitesTabProviderProps> = ({
  sites,
  profileId,
  profileName,
  onProfileSitesUpdated,
}) => {
  const spaceInfo: SpaceInfo = {
    item: { id: profileId, name: profileName, contentType: 'serveradminprofiles' as any },
    spaceType: 'serveradminprofiles',
    subspaceName: 'sites',
  };

  useEffect(() => {
    const store = getAppStore();
    const contentItems = sites.map(convertSiteToContentInfo);
    const listResult = new ListResult(contentItems, 0, contentItems.length);
    store.dispatch(spaceContentsRequestSucceeded(listResult));
    store.dispatch(contentCountsUpToDate());
  }, [sites]);

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedExplorer
        supportedDisplayModes={['list']}
        disableConnectedToolbar={true}
        toolbarProps={{
          supportFilterPanel: false,
          hideSelection: true,
          toolbarVisibility: 'enabled',
          toolbarContentSpecificControls: {
            beginning: adminProfileSiteManagementButton({ profileId, profileName, sites, onProfileSitesUpdated }),
          },
        }}
        emptyStateProps={{
          primaryMessage: Messages.adminProfileSitesEmptyTitle(),
          secondaryMessage: Messages.adminProfileSitesEmptyDescription(),
        }}
      />
    </ConnectedSpaceInfoProvider>
  );
};

AdminProfileSitesTabProvider.displayName = 'AdminProfileSitesTabProvider';
